import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { DevToolsBar } from '@/components/dev/DevToolsBar';
import { ElementTree, collectPaths, pathsMatching, KIND_STYLES } from '@/components/dev/ElementTree';
import { DOMAINS, NODE_KINDS, countNodes, countWarnings, type FeatureTree, type NodeKind } from '@/lib/data/elementTrees';

const ALL_FEATURES: { domainId: string; feature: FeatureTree }[] =
  DOMAINS.flatMap(d => d.features.map(feature => ({ domainId: d.id, feature })));

export const ArchitecturePage = () => {
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string>(DOMAINS[0].features[0].id);
  const [query, setQuery] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const q = query.trim().toLowerCase();

  // Which features match the query (label or ref anywhere in their tree)
  const matchSet = useMemo(() => {
    if (!q) return null;
    const s = new Set<string>();
    for (const { feature } of ALL_FEATURES) {
      const tmp = new Set<string>();
      if (pathsMatching(feature.root, feature.id, q, tmp) || tmp.size > 0) s.add(feature.id);
    }
    return s;
  }, [q]);

  const selected = useMemo(
    () => ALL_FEATURES.find(f => f.feature.id === selectedId)?.feature ?? DOMAINS[0].features[0],
    [selectedId],
  );

  // Expansion state for the selected tree. Default: expand first two levels.
  const defaultExpanded = useMemo(() => {
    const all = collectPaths(selected.root, selected.id);
    return new Set(all.filter(p => (p.match(/\./g)?.length ?? 0) <= 1)); // root + level-1
  }, [selected]);

  const [expandedByFeature, setExpandedByFeature] = useState<Record<string, Set<string>>>({});

  // When searching, auto-expand to every match in the selected tree.
  const searchExpanded = useMemo(() => {
    if (!q) return null;
    const s = new Set<string>();
    pathsMatching(selected.root, selected.id, q, s);
    s.add(selected.id);
    return s;
  }, [q, selected]);

  const expanded = searchExpanded ?? expandedByFeature[selected.id] ?? defaultExpanded;

  const setExpanded = (next: Set<string>) =>
    setExpandedByFeature(prev => ({ ...prev, [selected.id]: next }));

  const toggle = (path: string) => {
    const next = new Set(expanded);
    next.has(path) ? next.delete(path) : next.add(path);
    setExpanded(next);
  };

  const expandAll = () => setExpanded(new Set(collectPaths(selected.root, selected.id)));
  const collapseAll = () => setExpanded(new Set([selected.id]));

  const toggleGroup = (id: string) =>
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // Totals
  const totalFeatures = ALL_FEATURES.length;
  const totalNodes = ALL_FEATURES.reduce((a, f) => a + countNodes(f.feature.root), 0);
  const totalWarnings = ALL_FEATURES.reduce((a, f) => a + countWarnings(f.feature.root), 0);
  const selectedWarnings = countWarnings(selected.root);
  const selectedNodes = countNodes(selected.root);

  return (
    <MainLayout aboveBreadcrumbs={<DevToolsBar active="architecture" />}>
      {/* Header */}
      <div className="sticky top-14 md:top-0 z-20 border-b border-gray-200 px-4 py-4 md:px-8 md:py-5 bg-gray-50/80 backdrop-blur-sm">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 rounded-md p-2">
              <span className="material-symbols-outlined text-primary text-xl">account_tree</span>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Architecture Map</h2>
              <p className="text-sm text-gray-500">Recursive element trees · {totalFeatures} features · {totalNodes} nodes</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {totalWarnings > 0 && (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">
                <span className="material-symbols-outlined text-sm">warning</span>
                {totalWarnings} flags
              </span>
            )}
            <a
              href="https://ops.ochrepoint.ca"
              onClick={(e) => { e.preventDefault(); navigate('/dev'); }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Dev Dashboard ›
            </a>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">

        {/* Left: feature navigation */}
        <aside className="lg:sticky lg:top-24 lg:self-start space-y-3">
          <div className="relative">
            <span className="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-lg">search</span>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search nodes, files, routes…"
              className="w-full rounded-lg border border-gray-200 pl-9 pr-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
            />
            {query && (
              <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <span className="material-symbols-outlined text-base">close</span>
              </button>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
            {DOMAINS.map(domain => {
              const groupCollapsed = collapsedGroups.has(domain.id);
              const visible = domain.features.filter(f => !matchSet || matchSet.has(f.id));
              if (matchSet && visible.length === 0) return null;
              return (
                <div key={domain.id} className="border-b border-gray-100 last:border-b-0">
                  <button
                    onClick={() => toggleGroup(domain.id)}
                    className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50"
                  >
                    <span className="material-symbols-outlined text-base text-gray-400">{domain.icon}</span>
                    <span className="text-xs font-bold uppercase tracking-wide text-gray-600 flex-1 text-left">{domain.label}</span>
                    <span className="text-[11px] text-gray-400">{visible.length}</span>
                    <span className={`material-symbols-outlined text-base text-gray-300 transition-transform ${groupCollapsed ? '' : 'rotate-180'}`}>expand_more</span>
                  </button>
                  {!groupCollapsed && visible.map(f => {
                    const warns = countWarnings(f.root);
                    const active = f.id === selectedId;
                    return (
                      <button
                        key={f.id}
                        onClick={() => setSelectedId(f.id)}
                        className={`w-full flex items-center gap-2 pl-9 pr-3 py-2 text-left text-sm transition-colors ${
                          active ? 'bg-primary/10 text-primary font-semibold' : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <span className="flex-1 truncate">{f.title}</span>
                        {warns > 0 && (
                          <span className="flex-shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-100 text-red-600 text-[10px] font-bold">{warns}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="rounded-xl border border-gray-200 bg-white p-3">
            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-2">Node types</p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
              {(Object.keys(NODE_KINDS) as NodeKind[]).map(k => (
                <div key={k} className="flex items-center gap-1.5">
                  <span className={`material-symbols-outlined text-[15px] ${KIND_STYLES[k].text}`}>{KIND_STYLES[k].icon}</span>
                  <span className="text-xs text-gray-600">{NODE_KINDS[k]}</span>
                </div>
              ))}
              <div className="flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[15px] text-red-500">warning</span>
                <span className="text-xs text-gray-600">Issue / drift</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Right: selected tree */}
        <section className="min-w-0">
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100 bg-gray-50/60 flex-wrap">
              <div className="min-w-0">
                <h3 className="font-bold text-gray-900 truncate">{selected.title}</h3>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {selected.route && (
                    <code className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">{selected.route}</code>
                  )}
                  {selected.file && (
                    <code className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{selected.file}</code>
                  )}
                  <span className="text-[11px] text-gray-400">{selectedNodes} nodes</span>
                  {selectedWarnings > 0 && (
                    <span className="text-[11px] text-red-600 font-medium">{selectedWarnings} issue{selectedWarnings !== 1 ? 's' : ''}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={expandAll} className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">unfold_more</span>Expand all
                </button>
                <button onClick={collapseAll} className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">unfold_less</span>Collapse
                </button>
              </div>
            </div>

            <div className="p-3 md:p-4 overflow-x-auto">
              <ElementTree root={selected.root} rootPath={selected.id} expanded={expanded} onToggle={toggle} query={q} />
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-3 px-1">
            Source of truth: <code className="font-mono">docs/ElementTrees.md</code> + <code className="font-mono">FeatureInventory.md</code> §6 (AI Quote Wizard).
            Refs are <code className="font-mono">file:line</code>.
          </p>
        </section>
      </div>
    </MainLayout>
  );
};
